import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import RenderField from "./subComponents/RenderField";
import { useCreateProject } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import * as Yup from "yup";
import { useFormik } from "formik";
import { getPrincipleEntitiesForSelect } from "../../../../../common/utils/utils";
import postService from "../../../../../api/services/post-service";
import {
  ADD_PROJECT,
  UPDATE_PROJECT,
} from "../../../../../api/endpoints/channel-endpoint";
import toast from "react-hot-toast";
import useBackdrop from "../../../../../components/custom-hooks/use-backdrop";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECT_POST_LIST } from "../../../../../queries/query-constants/workspace/workspace-constant";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import ReactMultiCreatableSelect from "../../../../../components/react-select/ReactMultiSelect";
import FormBuilder from "./subComponents/FormBuilder";

const DocTubeForm = ({
  project_type_code,
  project_group_id,
  project_type_id,
  projectDetails = [],
  channelData = [],
}) => {
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { redirectTo } = useRedirect();
  const queryClient = useQueryClient();
  const { data: createProjectData, isLoading: principalEntitiesLoading } =
    useCreateProject();
  const [createdTags, setCreatedTags] = useState([]);
  const [isEditProject, setIsEditProject] = useState(false);

  const formik = useFormik({
    initialValues: {
      project_name: "",
      project_desc: "",
      principal_entity_id: "",
      project_attributes: "",
      project_start_date: "",
      project_end_date: "",
      project_owner: "",
      project_contact_name: "",
      project_contact_email: "",
      project_contact_phone: "",
      project_type_id: project_type_id,
      project_group_id: project_group_id,
      project_status: 1,
      project_po_ref: "",
      project_po_date: "",
      project_logo: "",
    },
    validationSchema: Yup.object({
      project_name: Yup.string().trim().required("Project Name is required"),
      project_desc: Yup.string()
        .trim()
        .required("Project Description is required"),
      principal_entity_id: Yup.string().required(
        "Principal Entity is required"
      ),
      project_start_date: Yup.date()
        .required("Start date is required")
        .test(
          "start_date_before_end_date",
          "Start date must be before the End date.",
          function (value) {
            const { project_end_date } = this.parent;
            return (
              !project_end_date ||
              !value ||
              new Date(value) < new Date(project_end_date)
            );
          }
        ),
      project_end_date: Yup.date()
        .required("End date is required")
        .test(
          "end_date_after_start_date",
          "End date must be after the Start date.",
          function (value) {
            const { project_start_date } = this.parent;
            return (
              !project_start_date ||
              !value ||
              new Date(value) > new Date(project_start_date)
            );
          }
        ),
      project_owner: Yup.string()
        .trim()
        .required("Project Owner name is required"),
      project_contact_name: Yup.string()
        .trim()
        .required("Project contact person name is required"),
      project_contact_email: Yup.string()
        .trim()
        .required("Project contact person Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Please enter a valid email address."
        ),
      project_contact_phone: Yup.string()
        .trim()
        .matches(/^[6-9]\d{9}$/, {
          message: "Please enter valid number.",
          excludeEmptyString: false,
        })
        .required("Project contact person phone is required"),
      project_po_ref: Yup.string()
        .trim()
        .required("Project PO Reference is required"),
      project_po_date: Yup.string()
        .trim()
        .required("Project PO Date is required"),
      project_logo: Yup.string().required("Project Logo is required"),
    }),
    onSubmit: async (values) => {
      const payload = { ...values };
      payload.project_status === 0
        ? (payload.project_status = 2)
        : (payload.project_status = 1);

      payload.project_attributes = [
        {
          key: "tags",
          value: JSON.stringify(createdTags),
        },
      ];
      payload.project_to_content = [];
      payload.project_to_event = [];
      payload.project_campaign_groups = [];

      try {
        showBackdrop();
        delete payload?.dbRef;
        let response;
        if (projectDetails.length > 0) {
          response = await postService(UPDATE_PROJECT, payload);
        } else {
          response = await postService(ADD_PROJECT, payload);
        }

        if (response?.data?.success) {
          queryClient.invalidateQueries({
            queryKey: [GET_PROJECT_POST_LIST],
          });
          toast.success("Project created successfully");
          redirectTo("workspace/doctube");
        } else {
          toast.error("Something went wrong");
        }
      } catch (e) {
        console.log(e);
        toast.error("Something went wrong");
      } finally {
        hideBackdrop();
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;

  useEffect(() => {
    if (projectDetails.length > 0) {
      setIsEditProject(true);
      preFillProjectForm();
    }
  }, []);

  function preFillProjectForm() {
    const projectStatus = projectDetails?.at(0)?.project_status === 2 ? 0 : 1;

    const projectTags = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "tags"
      )?.attribute_value;
    const projectTagsArray = projectTags ? JSON.parse(projectTags) : [];
    setCreatedTags(projectTagsArray);

    setValues({
      ...values,
      ...projectDetails?.at(0),
      project_status: projectStatus,
    });
  }

  const onTagsChangeHandler = (selectedValue) => {
    setCreatedTags(selectedValue);
  };

  const fields = [
    {
      row: 1,
      columns: [
        {
          id: 1,
          label: "Project Name",
          field_name: "project_name",
          type: "text",
          placeholder: "Enter Project Name",
          required: true,
          colSize: 6,
        },
        {
          id: 2,
          label: "Principal Entity",
          field_name: "principal_entity_id",
          type: "select",
          options: getPrincipleEntitiesForSelect(
            createProjectData?.principal_entities || []
          ),
          required: true,
          optionLabelKey: "principal_entity_name",
          optionValueKey: "principal_entity_id",
          placeholder: "Select Principal Entity",
          colSize: 3,
        },
        {
          id: 3,
          label: "Project Status",
          field_name: "project_status",
          type: "switch",
          required: false,
          disabled: !isEditProject,
          colSize: 3,
        },
      ],
    },
    {
      row: 2,
      columns: [
        {
          id: 1,
          label: "Project Description",
          field_name: "project_desc",
          type: "textarea",
          placeholder: "Enter Project Description",
          required: true,
        },
        {
          id: 2,
          label: "Project Logo",
          field_name: "project_logo",
          type: "file",
          required: true,
        },
      ],
    },
    {
      row: 3,
      columns: [
        {
          id: 1,
          label: "Project Owner Name",
          field_name: "project_owner",
          type: "text",
          placeholder: "Enter Project Owner",
          required: true,
        },
        {
          id: 2,
          label: "Project Start Date",
          field_name: "project_start_date",
          type: "date",
          required: true,
        },
        {
          id: 3,
          label: "Project End Date",
          field_name: "project_end_date",
          type: "date",
          required: true,
        },
      ],
    },
    {
      row: 4,
      columns: [
        {
          id: 1,
          label: "Project Contact Person Name",
          field_name: "project_contact_name",
          type: "text",
          placeholder: "Enter Owner Contact",
          required: true,
        },
        {
          id: 2,
          label: "Project Contant Person Email",
          field_name: "project_contact_email",
          type: "text",
          placeholder: "Enter Owner Email",
          required: true,
        },
        {
          id: 3,
          label: "Project Contact Person Phone",
          field_name: "project_contact_phone",
          type: "number",
          placeholder: "Enter Owner Phone",
          required: true,
        },
      ],
    },
    {
      row: 5,
      columns: [
        {
          id: 1,
          label: "Purchase Order Reference",
          field_name: "project_po_ref",
          type: "text",
          placeholder: "Enter Purchase Order Reference",
          required: true,
        },
        {
          id: 2,
          label: "Purchase Order Date",
          field_name: "project_po_date",
          type: "date",
          placeholder: "Enter Purchase Order Date",
          required: true,
        },
      ],
    },
    {
      row: 6,
      columns: [
        {
          id: 1,
          label: "Project Tags",
          field_name: "tags",
          type: "creatableSelect",
          required: false,
          options: createdTags,
          onCustomChange: onTagsChangeHandler,
        },
      ],
    },
  ];

  return (
    <Form onSubmit={handleSubmit}>
      <FormBuilder
        fields={fields}
        handleChange={handleChange}
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
      />

      <div className="d-flex justify-content-end gap-3">
        <Button
          variant="secondary"
          onClick={() => redirectTo("workspace/doctube")}
        >
          Cancel
        </Button>
        <Button type="submit">{`${
          projectDetails.length > 0 ? "Update" : "Submit"
        }`}</Button>
      </div>
    </Form>
  );
};

export default DocTubeForm;
